import { Box } from '@mui/material';
import { EventsExtension } from '@remirror/extension-events';
import {
  TableExtension as ReactTableExtension,
  TableComponents,
} from '@remirror/extension-react-tables';
import { Remirror, useRemirror } from '@remirror/react';
import { AllStyledComponent } from '@remirror/styles/emotion';
import type { AnyExtension } from 'remirror';
import {
  BoldExtension,
  BulletListExtension,
  CodeBlockExtension,
  CodeExtension,
  HistoryExtension,
  HorizontalRuleExtension,
  ItalicExtension,
  LinkExtension,
  MarkdownExtension,
  OrderedListExtension,
  ParagraphExtension,
  PlaceholderExtension,
  StrikeExtension,
  TableExtension,
  TaskListExtension,
  UnderlineExtension,
} from 'remirror/extensions';
import {
  ContextAccessor,
  EditorComponent,
  FloatingLinkToolbar,
  Toolbar,
} from './components';
import { CustomHeadingExtension } from './components/custom-heading';
import { StreamCaretExtension, StreamTokenExtension } from './extensions';

// ProseMirror forces translate="no" on the editor element, so opting back into
// browser translation means overriding it explicitly.
const TRANSLATABLE_ATTRIBUTES = { translate: 'yes' };
const UNTRANSLATABLE_ATTRIBUTES = {};

interface MarkdownEditorProps {
  editable?: boolean;
  markdown: string;
  onBlur?: () => void;
  onDiscard?: () => void;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onSave?: () => void;
  placeholder?: string;
  translatable?: boolean;
}

export const MarkdownEditor = ({
  editable,
  markdown,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  translatable = !editable,
}: MarkdownEditorProps) => {
  const { manager } = useRemirror({
    ...(editable && { builtin: { persistentSelectionClass: 'selection' } }),
    content: markdown,
    extensions: (): AnyExtension[] => [
      new EventsExtension({}),
      new MarkdownExtension({}),
      new CustomHeadingExtension({}),
      new BoldExtension({}),
      new ItalicExtension({}),
      new StrikeExtension({}),
      new UnderlineExtension({}),
      new BulletListExtension({}),
      new OrderedListExtension({}),
      new CodeBlockExtension({}),
      new CodeExtension({}),
      new HistoryExtension({}),
      new HorizontalRuleExtension({}),
      new LinkExtension({
        autoLink: true,
        defaultTarget: '_blank',
        openLinkOnClick: false,
      }),
      new ParagraphExtension({}),
      new PlaceholderExtension({
        placeholder,
      }),
      editable
        ? (new ReactTableExtension({}) as AnyExtension)
        : new TableExtension({}),
      new StreamCaretExtension({}),
      new StreamTokenExtension({}),
      new TaskListExtension({}),
    ],
    selection: 'start',
    stringHandler: 'markdown',
  });

  return (
    <AllStyledComponent>
      <Remirror
        attributes={
          translatable ? TRANSLATABLE_ATTRIBUTES : UNTRANSLATABLE_ATTRIBUTES
        }
        editable={editable}
        manager={manager}
        onChange={(parameter) => {
          // expose content to parent component so we
          // can honor external validation rules
          const markdown = parameter.helpers.getMarkdown();
          onChange?.(markdown);
        }}
      >
        {editable && (
          <>
            <Box mb={2}>
              <Toolbar />
            </Box>
            <TableComponents enableTableCellMenu={false} />
          </>
        )}
        <EditorComponent />
        {editable && <FloatingLinkToolbar />}
        <ContextAccessor
          markdown={markdown}
          onBlur={onBlur}
          onFocus={onFocus}
        />
      </Remirror>
    </AllStyledComponent>
  );
};
